// Copyright (c) Docugami, Inc. All rights reserved.

import axios from "axios";
import { Config } from "config";
import dayjs from "dayjs";
import { ProviderId, StorageProviderAuthenticationResponse, StorageProviderTokenRequest, UploadDocumentsStatus, UploadDocumentsRequest, UploadStatus } from "networking/StorageProviders";
import { IObservableCollection, ObservableArray } from "utils/ObservableArray";
import { RefCount } from "utils/RefCount";
import { RetryConfiguration, RetryResult } from "utils/RetryPromise";
import { StorageKey } from "utils/StorageAccessor";
import { TrackedRequest, TrackedRequests } from "utils/TrackedRequests";
import { Event, IEvent } from "utils/Event";
import { Token, TokenResponse } from "networking/Authentication";
const { v4: uuidv4 } = require('uuid');

export interface FileUploadCounts {
    totalCount: number;
    failedCount: number;
    succeededCount: number;
}

export interface IStorageManager {
    // Authentication
    requestToken: (providerId: ProviderId) => Promise<TokenResponse>;
    authenticate: (providerId: ProviderId, requestId: string) => Promise<StorageProviderAuthenticationResponse>;

    // Uploads
    uploadRequest: (request: UploadDocumentsRequest) => Promise<string>;
    setOrgId: (orgId: string) => void;
    enableUploadStatus(): () => void;
    activeUploads: IObservableCollection<UploadDocumentsStatus>;
    onFileUpload: IEvent<FileUploadCounts>;
}

class StorageManager implements IStorageManager {
    private readonly _tokenCache: Map<ProviderId, Token> = new Map();

    // Upload status tracking
    private _uploadRequests!: TrackedRequests<UploadDocumentsStatus>;
    private readonly _fileUploadEvent: Event<FileUploadCounts> = new Event();
    private readonly _shouldPollUploadStatusResultCallback: (result: UploadDocumentsStatus) => boolean;
    private readonly _shouldPollUploadStatusErrorCallback: (error: any) => boolean;
    private readonly _onUploadStatusResultCallback: (request: TrackedRequest, result: RetryResult<UploadDocumentsStatus>) => void;
    private _activeUploads: ObservableArray<UploadDocumentsStatus> = new ObservableArray<UploadDocumentsStatus>([], (item1, item2) => item1.instanceId === item2.instanceId);
    private _uploadReferences!: RefCount;
    private _orgId: string = '';

    get activeUploads(): IObservableCollection<UploadDocumentsStatus> {
        return this._activeUploads;
    }

    get onFileUpload(): IEvent<FileUploadCounts> { return this._fileUploadEvent; }

    constructor() {
        this._shouldPollUploadStatusResultCallback = this.shouldPollUploadStatusResult.bind(this);
        this._shouldPollUploadStatusErrorCallback = this.shouldPollUploadStatusError.bind(this);
        this._onUploadStatusResultCallback = this.onUploadStatusResult.bind(this);
        this.initializeUploadRequests();
    }

    setOrgId(orgId: string): void {
        if (!orgId) {
            throw new Error('Invalid argument: orgId should be nonempty string.')
        }

        // only update the orgId if it has changed
        if (orgId === this._orgId) {
            return;
        }

        this._orgId = orgId;
        this.initializeUploadRequests();
    }

    enableUploadStatus(): () => void {
        return this._uploadReferences.acquire();
    }

    async requestToken(providerId: ProviderId): Promise<TokenResponse> {
        if (this._tokenCache.has(providerId)) {
            const cachedToken = this._tokenCache.get(providerId)!;
            if (!isTokenExpired(cachedToken, 180)) {
                return {
                    accessToken: cachedToken
                }
            }
        }

        // Perform the request
        const input: StorageProviderTokenRequest = {
            providerId: providerId
        };

        return await this.performTokenRequest(input);
    }

    async authenticate(providerId: ProviderId, requestId: string): Promise<StorageProviderAuthenticationResponse> {
        const url = `${Config.fileManagerApiUrl}/storage/auth/status/${encodeURIComponent(requestId)}`
        const authResponse = await axios.get<StorageProviderAuthenticationResponse>(url);

        if (!!authResponse.data.accessToken) {
            this._tokenCache.set(providerId, authResponse.data.accessToken);
        }

        return authResponse.data;
    }

    async uploadRequest(request: UploadDocumentsRequest): Promise<string> {
        if (request.items.length === 0) {
            throw new Error("Cannot upload an empty item set.");
        }

        // Optimistic UX updates
        const tempStatus: UploadDocumentsStatus = {
            instanceId: uuidv4(),
            providerId: request.providerId,
            status: UploadStatus.GatheringFiles,
            timeStamp: new Date(),
            totalCount: 0,
            failedCount: 0,
            succeededCount: 0
        }

        const url = `${Config.fileManagerApiUrl}/storage/${encodeURIComponent(request.providerId)}/upload`

        let instanceId: string;
        try {
            const response = await axios.post<string>(url, request);
            instanceId = response.data;
            tempStatus.instanceId = instanceId;
        }
        catch (error: any) {
            tempStatus.status = UploadStatus.Failed;
            throw error;
        }

        this.handleUploadStatusResult(tempStatus);

        this._uploadRequests.track({
            id: instanceId,
            url: `${Config.fileManagerApiUrl}/storage/${encodeURIComponent(request.providerId)}/upload/${encodeURIComponent(instanceId)}`,
            timestamp: new Date()
        });

        return instanceId;
    }

    private initializeUploadRequests() {
        const config: RetryConfiguration<UploadDocumentsStatus> = {
            intervalMs: 2000,
            maxRetryCount: 300, // 10 minutes
            resultHandler: this._shouldPollUploadStatusResultCallback,
            errorHandler: this._shouldPollUploadStatusErrorCallback
        };

        this._uploadRequests = new TrackedRequests<UploadDocumentsStatus>(StorageKey.StorageProviderUploads, config, this._orgId);
        this._uploadReferences = new RefCount(
            () => this._uploadRequests.start(this._onUploadStatusResultCallback),
            () => {
                this._uploadRequests.stop();
                this._activeUploads.reset();
            });
    }

    private async performTokenRequest(input: StorageProviderTokenRequest): Promise<TokenResponse> {
        this._tokenCache.delete(input.providerId);

        // Make the initial request to get the token or initiate the authentication flow
        const url = `${Config.fileManagerApiUrl}/storage/auth/token`;
        const response = await axios.post<TokenResponse>(url, input);

        if (!!response.data.accessToken) {
            this._tokenCache.set(input.providerId, response.data.accessToken);
        }

        return response.data;
    }

    private shouldPollUploadStatusResult(status: UploadDocumentsStatus): boolean {
        const isComplete = status.status === UploadStatus.Complete || status.status === UploadStatus.Failed;
        return !isComplete;
    }

    private shouldPollUploadStatusError(error: any): boolean {
        // Stop polling on failures
        return false;
    }

    private onUploadStatusResult(request: TrackedRequest, result: RetryResult<UploadDocumentsStatus>) {
        if (!!result.result) {
            this.handleUploadStatusResult(result.result);
        }
        else if (result.isComplete) {
            this._activeUploads.removeByKeyValue("instanceId", request.id);
        }
    }

    private handleUploadStatusResult(status: UploadDocumentsStatus) {
        const isComplete = status.status === UploadStatus.Complete || status.status === UploadStatus.Failed;

        const event = {
            totalCount: status.totalCount,
            failedCount: status.failedCount,
            succeededCount: status.succeededCount
        };

        if (this._activeUploads.items.find(item => item.instanceId === status.instanceId &&
            item.status === UploadStatus.GatheringFiles &&
            status.status === UploadStatus.Uploading)) {
            this._fileUploadEvent.raise(event);
        }

        this._activeUploads.addOrUpdate(status);

        if (isComplete) {
            this._activeUploads.remove(status);
            this._fileUploadEvent.raise(event);
        }
    }
}

export function isTokenExpired(token: Token, validityWindowS: number): boolean {
    const tokenDate = dayjs(new Date(token.createdAt));
    const expirationDate = tokenDate.add(token.expirationSeconds, 'seconds');
    const checkDate = dayjs(new Date()).add(validityWindowS, 'seconds');

    return checkDate >= expirationDate;
}

export const StorageManagerInstance: IStorageManager = new StorageManager();
